import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AuthService } from 'src/app/auth/auth.service';
import { AiColorTaggerModelOptions, SettingsService } from '../settings/services/settings.service';

interface BuildNodesAutoScaling {
  enabled: boolean;
  itemsMaxScale: number;
  itemsMinScale: number;
  challengesMaxScale: number;
  challengesMinScale: number;
  imagesMaxScale: number;
  imagesMinScale: number;
  minigamesMaxScale: number;
  minigamesMinScale: number;
  itemsCurrentScale?: number;
  challengesCurrentScale?: number;
  imagesCurrentScale?: number;
  minigamesCurrentScale?: number;
}

interface GridlySettings {
  templateId?: string | null;
  dbs?: { id: string; env: string }[];
}

interface CmsSettingsRecord {
  id: number;
  name: string;
  autoPromoteEnabled?: boolean;
  autoProdPushEnabled?: boolean;
  autoDevQAPushEnabled?: boolean;
  manualDevQAGamedataPushEnabled?: boolean;
  manualDevGamedataPushEnabled?: boolean;
  manualQAGamedataPushEnabled?: boolean;
  syncResourcesButtonEnabled?: boolean;
  manualProdPushButtonEnabled?: boolean;
  aiColorTaggingEnabled?: boolean;
  aiColorTaggerModel?: string;
  aiColorTaggerSessionCostLimit?: number | null;
  aiColorTaggerMonthlyCostLimit?: number | null;
  aiTaggingEnabled?: boolean;
  gamedataInProgress?: boolean;
  bullJobPruningIsEnable?: boolean;
  autoNodesScaleUp?: boolean;
  autoNodesScaleDown?: boolean;
  itemsNodesScaleUpValue?: number;
  itemsNodesScaleDownValue?: number;
  levelsNodesScaleUpValue?: number;
  levelsNodesScaleDownValue?: number;
  imagesNodesScaleUpValue?: number;
  imagesNodesScaleDownValue?: number;
  buildNodesAutoScaling?: BuildNodesAutoScaling;
  gridlySettings?: GridlySettings;
  processingCreationOnGridly?: string[];
  processingMigrationOnGridly?: string[];
  processingCreationForAi?: string[];
  processingMigrationForAi?: string[];
}

interface CmsSettingsUpdateDto {
  autoPromoteEnabled: boolean;
  autoProdPushEnabled: boolean;
  autoDevQAPushEnabled: boolean;
  manualDevQAGamedataPushEnabled: boolean;
  manualDevGamedataPushEnabled: boolean;
  manualQAGamedataPushEnabled: boolean;
  syncResourcesButtonEnabled: boolean;
  manualProdPushButtonEnabled: boolean;
  aiColorTaggingEnabled: boolean;
  aiColorTaggerModel: string;
  aiColorTaggerSessionCostLimit: number | null;
  aiColorTaggerMonthlyCostLimit: number | null;
  aiTaggingEnabled: boolean;
  bullJobPruningIsEnable: boolean;
  autoNodesScaleUp: boolean;
  autoNodesScaleDown: boolean;
  itemsNodesScaleUpValue: number;
  itemsNodesScaleDownValue: number;
  levelsNodesScaleUpValue: number;
  levelsNodesScaleDownValue: number;
  imagesNodesScaleUpValue: number;
  imagesNodesScaleDownValue: number;
  buildNodesAutoScaling: BuildNodesAutoScaling;
  gridlySettings: GridlySettings;
  processingCreationOnGridly: string[];
  processingMigrationOnGridly: string[];
  processingCreationForAi: string[];
  processingMigrationForAi: string[];
}

@Component({
  selector: 'app-cms-dev-settings',
  templateUrl: './cms-dev-settings.component.html',
  styleUrls: ['./cms-dev-settings.component.sass'],
  providers: [ConfirmationService, MessageService],
})
export class CmsDevSettingsComponent implements OnInit {
  settingsForm: UntypedFormGroup;
  cmsSettings: CmsSettingsRecord | null = null;
  buildNodesSettings: CmsSettingsRecord | null = null;
  fetchedAt: Date;
  fetchedAtBuildNodes: Date;
  isLoading = true;
  isSaving = false;
  /** AI cost limits cap real spend, so only admins can change them (the API enforces the same rule). */
  get canEditCostLimits(): boolean {
    return this.authService.hasRole('ROLE_ADMIN');
  }

  /** Models with a known price — the only ones the color tag preview can estimate and the worker can price. */
  aiColorTaggerModels: string[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private settingsService: SettingsService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    public authService: AuthService,
  ) {}

  async ngOnInit() {
    this.buildForm();
    await this.loadSettings();
  }

  private buildForm() {
    this.settingsForm = this.fb.group({
      // CMS tab
      autoPromoteEnabled: [false],
      autoProdPushEnabled: [false],
      autoDevQAPushEnabled: [false],
      manualDevQAGamedataPushEnabled: [false],
      manualDevGamedataPushEnabled: [false],
      manualQAGamedataPushEnabled: [false],
      syncResourcesButtonEnabled: [false],
      manualProdPushButtonEnabled: [false],
      aiColorTaggingEnabled: [true],
      aiColorTaggerModel: [''],
      // USD; empty means no limit. Admin-only — disabled below for everyone else, and the API enforces it.
      aiColorTaggerSessionCostLimit: [null, [Validators.min(0)]],
      aiColorTaggerMonthlyCostLimit: [null, [Validators.min(0)]],
      aiTaggingEnabled: [false],
      gamedataInProgress: [{ value: false, disabled: true }],
      bullJobPruningIsEnable: [false],
      // Asset Builder tab
      autoNodesScaleUp: [true],
      autoNodesScaleDown: [true],
      itemsNodesScaleUpValue: [5, [Validators.required, Validators.min(1)]],
      itemsNodesScaleDownValue: [1, [Validators.required, Validators.min(1)]],
      levelsNodesScaleUpValue: [5, [Validators.required, Validators.min(1)]],
      levelsNodesScaleDownValue: [1, [Validators.required, Validators.min(1)]],
      imagesNodesScaleUpValue: [4, [Validators.required, Validators.min(1)]],
      imagesNodesScaleDownValue: [1, [Validators.required, Validators.min(1)]],
      buildNodesAutoScalingEnabled: [false],
      buildNodesAutoScalingItemsMaxScale: [5, [Validators.required, Validators.min(0)]],
      buildNodesAutoScalingItemsMinScale: [1, [Validators.required, Validators.min(0)]],
      buildNodesAutoScalingChallengesMaxScale: [5, [Validators.required, Validators.min(0)]],
      buildNodesAutoScalingChallengesMinScale: [1, [Validators.required, Validators.min(0)]],
      buildNodesAutoScalingImagesMaxScale: [5, [Validators.required, Validators.min(0)]],
      buildNodesAutoScalingImagesMinScale: [1, [Validators.required, Validators.min(0)]],
      buildNodesAutoScalingMinigamesMaxScale: [5, [Validators.required, Validators.min(0)]],
      buildNodesAutoScalingMinigamesMinScale: [0, [Validators.required, Validators.min(0)]],
      // Localization tab
      gridlyTemplateId: ['', [Validators.pattern(/^[\w-]*$/)]],
      processingCreationOnGridly: [[]],
      processingMigrationOnGridly: [[]],
      processingCreationForAi: [[]],
      processingMigrationForAi: [[]],
    });
    if (!this.canEditCostLimits) {
      this.settingsForm.get('aiColorTaggerSessionCostLimit')?.disable();
      this.settingsForm.get('aiColorTaggerMonthlyCostLimit')?.disable();
    }
  }

  private async loadSettings() {
    try {
      this.isLoading = true;
      let aiModelOptions: AiColorTaggerModelOptions | null;
      [this.cmsSettings, this.buildNodesSettings, aiModelOptions] = await Promise.all([
        this.settingsService.getSettingByQuery({ query: { name: 'cms-settings' } }),
        this.settingsService.getSettingByQuery({ query: { name: 'buildnodes-scaling' } }).catch(() => null),
        this.settingsService.getAiColorTaggerModels().catch(() => null),
      ]);
      this.aiColorTaggerModels = aiModelOptions?.models ?? [];
      this.fetchedAt = new Date();
      this.fetchedAtBuildNodes = new Date();

      if (this.cmsSettings) {
        const s = this.cmsSettings;
        const bnas: Partial<BuildNodesAutoScaling> = this.buildNodesSettings?.buildNodesAutoScaling ?? {};
        this.settingsForm.patchValue({
          autoPromoteEnabled: !!s.autoPromoteEnabled,
          autoProdPushEnabled: !!s.autoProdPushEnabled,
          autoDevQAPushEnabled: !!s.autoDevQAPushEnabled,
          manualDevQAGamedataPushEnabled: !!s.manualDevQAGamedataPushEnabled,
          manualDevGamedataPushEnabled: !!s.manualDevGamedataPushEnabled,
          manualQAGamedataPushEnabled: !!s.manualQAGamedataPushEnabled,
          syncResourcesButtonEnabled: !!s.syncResourcesButtonEnabled,
          manualProdPushButtonEnabled: !!s.manualProdPushButtonEnabled,
          aiColorTaggingEnabled: s.aiColorTaggingEnabled !== false,
          // Unset means the default model, so show that rather than an empty dropdown.
          aiColorTaggerModel: s.aiColorTaggerModel || aiModelOptions?.defaultModel || '',
          aiColorTaggerSessionCostLimit: s.aiColorTaggerSessionCostLimit ?? null,
          aiColorTaggerMonthlyCostLimit: s.aiColorTaggerMonthlyCostLimit ?? null,
          aiTaggingEnabled: s.aiTaggingEnabled === true,
          gamedataInProgress: !!s.gamedataInProgress,
          bullJobPruningIsEnable: !!s.bullJobPruningIsEnable,
          autoNodesScaleUp: s.autoNodesScaleUp !== false,
          autoNodesScaleDown: s.autoNodesScaleDown !== false,
          itemsNodesScaleUpValue: s.itemsNodesScaleUpValue ?? 5,
          itemsNodesScaleDownValue: s.itemsNodesScaleDownValue ?? 1,
          levelsNodesScaleUpValue: s.levelsNodesScaleUpValue ?? 5,
          levelsNodesScaleDownValue: s.levelsNodesScaleDownValue ?? 1,
          imagesNodesScaleUpValue: s.imagesNodesScaleUpValue ?? 4,
          imagesNodesScaleDownValue: s.imagesNodesScaleDownValue ?? 1,
          buildNodesAutoScalingEnabled: !!bnas.enabled,
          buildNodesAutoScalingItemsMaxScale: bnas.itemsMaxScale ?? 5,
          buildNodesAutoScalingItemsMinScale: bnas.itemsMinScale ?? 1,
          buildNodesAutoScalingChallengesMaxScale: bnas.challengesMaxScale ?? 5,
          buildNodesAutoScalingChallengesMinScale: bnas.challengesMinScale ?? 1,
          buildNodesAutoScalingImagesMaxScale: bnas.imagesMaxScale ?? 5,
          buildNodesAutoScalingImagesMinScale: bnas.imagesMinScale ?? 1,
          buildNodesAutoScalingMinigamesMaxScale: bnas.minigamesMaxScale ?? 5,
          buildNodesAutoScalingMinigamesMinScale: bnas.minigamesMinScale ?? 0,
          gridlyTemplateId: s.gridlySettings?.templateId ?? '',
          processingCreationOnGridly: s.processingCreationOnGridly ?? [],
          processingMigrationOnGridly: s.processingMigrationOnGridly ?? [],
          processingCreationForAi: s.processingCreationForAi ?? [],
          processingMigrationForAi: s.processingMigrationForAi ?? [],
        });
        this.settingsForm.markAsPristine();
      }
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to load CMS settings.',
      });
    } finally {
      this.isLoading = false;
    }
  }

  onSaveClick() {
    if (this.settingsForm.invalid) {
      this.settingsForm.markAllAsTouched();
      return;
    }

    this.confirmationService.confirm({
      message: 'You are about to change live CMS settings. These changes take effect immediately and may affect all users. Are you sure you want to proceed?',
      header: 'Confirm Settings Change',
      icon: 'pi pi-exclamation-triangle',
      accept: () => void this.saveSettings(),
    });
  }

  private async saveSettings() {
    if (!this.cmsSettings) {
      return;
    }

    try {
      this.isSaving = true;
      const v = this.settingsForm.getRawValue();
      const existingAutoScaling = this.buildNodesSettings?.buildNodesAutoScaling;
      const isDirty = (key: string) => !!this.settingsForm.get(key)?.dirty;

      const dto: Partial<CmsSettingsUpdateDto> = {};

      if (isDirty('autoPromoteEnabled')) dto.autoPromoteEnabled = v.autoPromoteEnabled;
      if (isDirty('autoProdPushEnabled')) dto.autoProdPushEnabled = v.autoProdPushEnabled;
      if (isDirty('autoDevQAPushEnabled')) dto.autoDevQAPushEnabled = v.autoDevQAPushEnabled;
      if (isDirty('manualDevQAGamedataPushEnabled')) dto.manualDevQAGamedataPushEnabled = v.manualDevQAGamedataPushEnabled;
      if (isDirty('manualDevGamedataPushEnabled')) dto.manualDevGamedataPushEnabled = v.manualDevGamedataPushEnabled;
      if (isDirty('manualQAGamedataPushEnabled')) dto.manualQAGamedataPushEnabled = v.manualQAGamedataPushEnabled;
      if (isDirty('syncResourcesButtonEnabled')) dto.syncResourcesButtonEnabled = v.syncResourcesButtonEnabled;
      if (isDirty('manualProdPushButtonEnabled')) dto.manualProdPushButtonEnabled = v.manualProdPushButtonEnabled;
      if (isDirty('aiColorTaggingEnabled')) dto.aiColorTaggingEnabled = v.aiColorTaggingEnabled;
      if (isDirty('aiColorTaggerModel')) dto.aiColorTaggerModel = v.aiColorTaggerModel;
      if (isDirty('aiColorTaggerSessionCostLimit')) dto.aiColorTaggerSessionCostLimit = v.aiColorTaggerSessionCostLimit;
      if (isDirty('aiColorTaggerMonthlyCostLimit')) dto.aiColorTaggerMonthlyCostLimit = v.aiColorTaggerMonthlyCostLimit;
      if (isDirty('aiTaggingEnabled')) dto.aiTaggingEnabled = v.aiTaggingEnabled;
      if (isDirty('bullJobPruningIsEnable')) dto.bullJobPruningIsEnable = v.bullJobPruningIsEnable;
      if (isDirty('autoNodesScaleUp')) dto.autoNodesScaleUp = v.autoNodesScaleUp;
      if (isDirty('autoNodesScaleDown')) dto.autoNodesScaleDown = v.autoNodesScaleDown;
      if (isDirty('itemsNodesScaleUpValue')) dto.itemsNodesScaleUpValue = v.itemsNodesScaleUpValue;
      if (isDirty('itemsNodesScaleDownValue')) dto.itemsNodesScaleDownValue = v.itemsNodesScaleDownValue;
      if (isDirty('levelsNodesScaleUpValue')) dto.levelsNodesScaleUpValue = v.levelsNodesScaleUpValue;
      if (isDirty('levelsNodesScaleDownValue')) dto.levelsNodesScaleDownValue = v.levelsNodesScaleDownValue;
      if (isDirty('imagesNodesScaleUpValue')) dto.imagesNodesScaleUpValue = v.imagesNodesScaleUpValue;
      if (isDirty('imagesNodesScaleDownValue')) dto.imagesNodesScaleDownValue = v.imagesNodesScaleDownValue;
      if (isDirty('processingCreationOnGridly')) dto.processingCreationOnGridly = v.processingCreationOnGridly;
      if (isDirty('processingMigrationOnGridly')) dto.processingMigrationOnGridly = v.processingMigrationOnGridly;
      if (isDirty('processingCreationForAi')) dto.processingCreationForAi = v.processingCreationForAi;
      if (isDirty('processingMigrationForAi')) dto.processingMigrationForAi = v.processingMigrationForAi;

      const buildNodesAutoScalingDirty = [
        'buildNodesAutoScalingEnabled',
        'buildNodesAutoScalingItemsMaxScale',
        'buildNodesAutoScalingItemsMinScale',
        'buildNodesAutoScalingChallengesMaxScale',
        'buildNodesAutoScalingChallengesMinScale',
        'buildNodesAutoScalingImagesMaxScale',
        'buildNodesAutoScalingImagesMinScale',
        'buildNodesAutoScalingMinigamesMaxScale',
        'buildNodesAutoScalingMinigamesMinScale',
      ].some(isDirty);

      if (buildNodesAutoScalingDirty && this.buildNodesSettings) {
        await this.settingsService.updateSettingV2(
          this.buildNodesSettings.id,
          {
            buildNodesAutoScaling: {
              itemsCurrentScale: existingAutoScaling?.itemsCurrentScale,
              challengesCurrentScale: existingAutoScaling?.challengesCurrentScale,
              imagesCurrentScale: existingAutoScaling?.imagesCurrentScale,
              minigamesCurrentScale: existingAutoScaling?.minigamesCurrentScale,
              enabled: v.buildNodesAutoScalingEnabled,
              itemsMaxScale: v.buildNodesAutoScalingItemsMaxScale,
              itemsMinScale: v.buildNodesAutoScalingItemsMinScale,
              challengesMaxScale: v.buildNodesAutoScalingChallengesMaxScale,
              challengesMinScale: v.buildNodesAutoScalingChallengesMinScale,
              imagesMaxScale: v.buildNodesAutoScalingImagesMaxScale,
              imagesMinScale: v.buildNodesAutoScalingImagesMinScale,
              minigamesMaxScale: v.buildNodesAutoScalingMinigamesMaxScale,
              minigamesMinScale: v.buildNodesAutoScalingMinigamesMinScale,
            },
          },
          this.fetchedAtBuildNodes,
        );
        this.fetchedAtBuildNodes = new Date();
      }

      if (isDirty('gridlyTemplateId')) {
        dto.gridlySettings = {
          dbs: this.cmsSettings.gridlySettings?.dbs,
          templateId: v.gridlyTemplateId || null,
        };
      }

      if (Object.keys(dto).length > 0) {
        await this.settingsService.updateSettingV2(this.cmsSettings.id, dto, this.fetchedAt);
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Saved',
        detail: 'CMS settings updated successfully.',
      });
      this.settingsForm.markAsPristine();
      this.fetchedAt = new Date();
    } catch (error: any) {
      const isConflict = error?.status === 409;
      this.messageService.add({
        severity: isConflict ? 'warn' : 'error',
        summary: isConflict ? 'Conflict Detected' : 'Error',
        detail: isConflict
          ? 'Settings were modified by another session. Reloading the latest values.'
          : 'Failed to save settings. Please try again.',
      });
      if (isConflict) {
        await this.loadSettings();
      }
    } finally {
      this.isSaving = false;
    }
  }
}
